import { useCallback, useEffect, useState } from 'react';

import { getGameEngine } from '../GameEngine';
import {
  DEFAULT_MUSIC_SETTINGS,
  type MusicSettings,
} from '../services/MusicEngine';
import {
  MUSIC_SETTINGS_STORAGE_KEY,
  applyMusicSettingsDefaults,
  loadMusicSettings,
  saveMusicSettings,
} from '../utils/MusicSettingsStorage';

export interface UseMusicSettingsResult {
  musicSettings: MusicSettings;
  setMusicSettings: (
    value: MusicSettings | ((prev: MusicSettings) => MusicSettings)
  ) => void;
  resetMusicSettings: () => void;
}

export const useMusicSettings = (): UseMusicSettingsResult => {
  const [musicSettings, setMusicSettingsState] = useState<MusicSettings>(() =>
    loadMusicSettings()
  );

  const applyToEngine = useCallback((settings: MusicSettings) => {
    try {
      const engine = getGameEngine();
      engine.setMusicSettings(settings);
      engine.startMusic();
    } catch {
      // Ignore attempts before the engine is initialized or during SSR/tests.
    }
  }, []);

  const updateState = useCallback(
    (updater: MusicSettings | ((prev: MusicSettings) => MusicSettings)) => {
      setMusicSettingsState(prevState => {
        const nextState =
          typeof updater === 'function'
            ? (updater as (prev: MusicSettings) => MusicSettings)(prevState)
            : updater;
        const normalized = applyMusicSettingsDefaults(nextState);
        saveMusicSettings(normalized);
        applyToEngine(normalized);
        return normalized;
      });
    },
    [applyToEngine]
  );

  useEffect(() => {
    applyToEngine(musicSettings);
  }, [applyToEngine, musicSettings]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === MUSIC_SETTINGS_STORAGE_KEY) {
        setMusicSettingsState(loadMusicSettings());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const resetMusicSettings = useCallback(() => {
    updateState(DEFAULT_MUSIC_SETTINGS);
  }, [updateState]);

  return {
    musicSettings,
    setMusicSettings: updateState,
    resetMusicSettings,
  };
};

export default useMusicSettings;
